import React, { useEffect, useState } from "react";
import { Plus, KeyRound, Trash2, Loader2, ShieldCheck, User as UserIcon, Wrench, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { authClient } from "../../lib/AuthContext";
import { Input } from "../../components/ui/input";

const ROLE_META = {
  manager: { icon: ShieldCheck, color: "#f28a16", label: "Manager" },
  admin: { icon: ShieldCheck, color: "#f28a16", label: "Manager" }, // legacy alias
  office_admin: { icon: ShieldCheck, color: "#0ea5e9", label: "Office Admin" },
  fitter: { icon: Wrench, color: "#3b82f6", label: "Fitter" },
  client: { icon: UserIcon, color: "#10b981", label: "Client" },
};

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "fitter", must_change_password: true });
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const load = async () => {
    setLoading(true);
    try { setUsers((await authClient.get("/auth/users")).data); }
    catch { toast.error("Could not load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authClient.post("/auth/users", form);
      toast.success(`${form.role} account created`);
      setShowNew(false);
      setForm({ email: "", password: "", name: "", role: "fitter", must_change_password: true });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create user"); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try { await authClient.put(`/auth/users/${u.id}`, { active: !u.active }); load(); }
    catch { toast.error("Could not update"); }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    if (!window.confirm(`Change ${u.email} from "${u.role}" to "${role}"?`)) return;
    try {
      await authClient.put(`/auth/users/${u.id}`, { role });
      toast.success(`Role changed to ${role}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not change role"); }
  };

  const changeEmail = async (u) => {
    const newEmail = window.prompt(`New email for this account:`, u.email);
    if (!newEmail || newEmail === u.email) return;
    try {
      await authClient.put(`/auth/users/${u.id}`, { email: newEmail.trim().toLowerCase() });
      toast.success("Email updated — the user will now log in with the new email.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not change email"); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Reset 2FA for ${u.email}? They'll be forced to re-enrol on next login.`)) return;
    try {
      await authClient.post(`/auth/users/${u.id}/reset-2fa`);
      toast.success("2FA reset — user will re-enrol on next login");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not reset 2FA"); }
  };

  const resetPassword = async () => {
    if (resetPw.length < 8) return toast.error("Password must be 8+ characters");
    try {
      await authClient.post(`/auth/users/${resetFor.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetFor.email} — they must change it on next login`);
      setResetFor(null); setResetPw("");
    } catch { toast.error("Reset failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email} permanently?`)) return;
    try { await authClient.delete(`/auth/users/${u.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black uppercase text-2xl sm:text-3xl md:text-4xl text-[#111619]">Users &amp; access</h1>
          <p className="text-[#3f4b52] mt-1 text-sm sm:text-base">Manager · Office Admin · Fitter accounts only. Public visitors do not need an account — they use the website's quote form.</p>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="user-new-btn"
          className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2">
          <Plus size={14} /> New user
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="mt-6 bg-white border border-[#cfd7db] rounded-md p-5 max-w-2xl grid grid-cols-2 gap-3" data-testid="user-new-form">
          <label className="col-span-2 sm:col-span-1"><span className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Email</span>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-white border-[#cfd7db] h-10" data-testid="user-email" />
          </label>
          <label className="col-span-2 sm:col-span-1"><span className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Full name</span>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-[#cfd7db] h-10" />
          </label>
          <label className="col-span-2 sm:col-span-1"><span className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Role</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="user-role">
              <option value="manager">Manager (full access + 2FA)</option>
              <option value="office_admin">Office Admin (day-to-day + 2FA)</option>
              <option value="fitter">Fitter (team portal)</option>
            </select>
            <span className="block text-[11px] text-[#9aa6ad] mt-1">
              Clients don't need accounts — the public quote form on the website turns them into a client record automatically. Managers can email them a passwordless magic-link portal to view their quote.
            </span>
          </label>
          <label className="col-span-2 sm:col-span-1"><span className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Temporary password (8+)</span>
            <Input type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 bg-white border-[#cfd7db] h-10 font-mono" data-testid="user-password" />
          </label>
          {form.role === "fitter" && (
            <label className="col-span-2"><span className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Fitter profile ID (matches team leader name on jobs)</span>
              <Input value={form.fitter_profile_id || ""} onChange={(e) => setForm({ ...form, fitter_profile_id: e.target.value })} className="mt-1 bg-white border-[#cfd7db] h-10" />
            </label>
          )}
          {(form.role === "manager" || form.role === "office_admin") && (
            <p className="col-span-2 text-xs text-[#3f4b52] bg-[#fff7ed] border border-[#f28a16]/30 rounded-md p-2">
              <ShieldCheck size={12} className="inline mr-1 text-[#f28a16]" />
              Two-factor sign-in is required for this role. A 6-digit code will be emailed to them at every login — no authenticator app needed.
            </p>
          )}
          <button type="submit" disabled={busy} data-testid="user-save-btn" className="col-span-2 bg-[#20282c] text-white h-11 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="animate-spin" size={14} />} Create user
          </button>
        </form>
      )}

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#3f4b52] inline-flex items-center gap-2 justify-center w-full"><Loader2 className="animate-spin" size={16} /> Loading…</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#edf2f4] text-[#3f4b52] uppercase tracking-widest text-xs">
              <tr>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Role</th>
                <th className="text-left px-4 py-2.5">2FA</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eef2f4]">
              {users.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.client;
                const Icon = meta.icon;
                const tf = u.two_factor || {};
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-mono text-[#111619]">
                      <button
                        type="button"
                        onClick={() => changeEmail(u)}
                        data-testid={`user-edit-email-${u.email}`}
                        className="hover:underline text-left"
                        title="Change this user's login email"
                      >
                        {u.email}
                      </button>
                    </td>
                    <td className="px-4 py-3">{u.name || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-sm text-white font-display uppercase tracking-widest" style={{ background: meta.color }}>
                          <Icon size={11} /> {meta.label}
                        </span>
                        <select
                          value={u.role}
                          onChange={(e) => changeRole(u, e.target.value)}
                          data-testid={`user-role-select-${u.email}`}
                          className="text-xs bg-white border border-[#cfd7db] rounded px-1 h-6"
                        >
                          <option value="manager">Manager</option>
                          <option value="office_admin">Office Admin</option>
                          <option value="fitter">Fitter</option>
                          {u.role === "client" && <option value="client">Client (legacy)</option>}
                        </select>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {tf.enabled ? (
                        <span className="inline-flex items-center gap-1 text-[#10b981]">
                          <ShieldCheck size={12} /> {(tf.method || "email") === "email" ? "Email OTP" : "Authenticator"}
                        </span>
                      ) : tf.require_on_next_login ? (
                        <span className="inline-flex items-center gap-1 text-[#f28a16]"><ShieldOff size={12} /> Setup pending</span>
                      ) : (
                        <span className="text-[#9aa6ad]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleActive(u)} className={`text-xs uppercase font-display tracking-widest px-2 py-0.5 rounded-sm ${u.active ? "bg-[#10b981] text-white" : "bg-[#9aa6ad] text-white"}`}>
                        {u.active ? "Active" : "Disabled"}
                      </button>
                      {u.must_change_password && <span className="ml-1 text-xs text-[#f28a16]">· must change PW</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        {tf.enabled && (
                          <button onClick={() => reset2fa(u)} className="text-[11px] text-[#3f4b52] px-2 h-8 border border-[#cfd7db] rounded-md inline-flex items-center gap-1 hover:bg-[#edf2f4]" data-testid={`user-reset-2fa-${u.email}`}>
                            <ShieldOff size={12} /> Reset 2FA
                          </button>
                        )}
                        <button onClick={() => setResetFor(u)} className="text-[11px] text-[#3f4b52] px-2 h-8 border border-[#cfd7db] rounded-md inline-flex items-center gap-1 hover:bg-[#edf2f4]" data-testid={`user-reset-${u.email}`}>
                          <KeyRound size={12} /> Reset PW
                        </button>
                        <button onClick={() => remove(u)} className="text-[11px] text-[#dc2626] px-2 h-8 border border-[#dc2626]/30 rounded-md inline-flex items-center gap-1 hover:bg-[#dc2626]/10">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {resetFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md p-5 max-w-sm w-full" data-testid="reset-pw-modal">
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Reset password</div>
            <div className="font-display font-bold text-[#111619] mt-0.5">For {resetFor.email}</div>
            <p className="text-xs text-[#3f4b52] mt-2">Set a new temporary password. The user will be forced to change it on next login.</p>
            <Input type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="New password (8+)" className="mt-3 bg-white border-[#cfd7db] h-10 font-mono" data-testid="reset-pw-input" />
            <div className="mt-3 flex gap-2 justify-end">
              <button onClick={() => { setResetFor(null); setResetPw(""); }} className="text-xs text-[#3f4b52] px-3 h-9">Cancel</button>
              <button onClick={resetPassword} data-testid="reset-pw-confirm" className="bg-[#20282c] text-white px-4 h-9 rounded-md font-display uppercase tracking-widest text-xs">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
